import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ChevronRight } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/community", label: "Community" },
  { to: "/challenges", label: "Challenges" },
  { to: "/guides", label: "Guides" },
  { to: "/success-stories", label: "Success Stories" },
  { to: "/newsletter", label: "Newsletter" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const linkClass = ({ isActive }) =>
    `text-[15px] font-medium transition-colors ${isActive ? "text-teal" : "text-plum-ink hover:text-teal"}`;

  return (
    <>
      <div className="bg-plum text-cream text-center text-sm py-2 px-4 grain" data-testid="announcement-bar">
        <Link to="/challenges/14-day-stronger-you" className="inline-flex items-center gap-1.5 hover:underline">
          <span className="font-script text-base text-rose">New!</span>
          Join the 14-Day Stronger You Challenge — start any day
          <ChevronRight className="h-4 w-4" />
        </Link>
      </div>

      <header className="sticky top-0 z-50 bg-cream/90 backdrop-blur-md border-b border-rose/50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 flex items-center justify-between h-[72px]">
          <Logo />

          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} className={linkClass} data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <div className="flex items-center gap-3">
                {user.role === "admin" && (
                  <Link to="/admin" className="text-sm font-medium text-mutedplum hover:text-teal" data-testid="nav-admin">Admin</Link>
                )}
                <span className="text-sm text-mutedplum">Hi, {user.name?.split(" ")[0]}</span>
                <button onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn"
                  className="rounded-full border-2 border-plum px-5 py-2 text-sm font-semibold text-plum hover:bg-plum hover:text-cream transition-colors">
                  Sign Out
                </button>
              </div>
            ) : (
              <>
                <Link to="/signin" data-testid="signin-btn"
                  className="text-[15px] font-semibold text-plum hover:text-teal transition-colors">Sign In</Link>
                <Link to="/join" data-testid="join-btn"
                  className="rounded-full bg-teal px-6 py-2.5 text-sm font-semibold text-white hover:bg-teal-dark transition-colors hover:-translate-y-0.5 duration-300">
                  Join the Circle
                </Link>
              </>
            )}
          </div>

          <button className="lg:hidden p-2 text-plum" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X /> : <Menu />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-rose/50 bg-cream px-4 py-4 space-y-1" data-testid="mobile-menu">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)}
                className="block py-2.5 text-plum-ink font-medium border-b border-rose/30">{n.label}</Link>
            ))}
            <div className="pt-4 flex flex-col gap-2">
              {user ? (
                <button onClick={() => { logout(); setOpen(false); navigate("/"); }}
                  className="rounded-full border-2 border-plum px-5 py-2.5 font-semibold text-plum">Sign Out</button>
              ) : (
                <>
                  <Link to="/signin" onClick={() => setOpen(false)} className="rounded-full border-2 border-plum px-5 py-2.5 text-center font-semibold text-plum">Sign In</Link>
                  <Link to="/join" onClick={() => setOpen(false)} className="rounded-full bg-teal px-5 py-2.5 text-center font-semibold text-white">Join the Circle</Link>
                </>
              )}
            </div>
          </div>
        )}
      </header>
    </>
  );
}
